import { isSuspensionJob, jobPaymentMeta, jobServiceSummary, normaliseJobStatus } from './utils'

export const PASSPORT_COMPONENT_TYPES = ['fork', 'shock', 'dropper']

const PASSPORT_TYPE_SET = new Set(PASSPORT_COMPONENT_TYPES)
const PRICE_SUFFIX_RE = /\s*(?:[-–]\s*)?(?:GBP|£)\s*[0-9]+(?:\.[0-9]{1,2})?/gi

function clean(value, max = 160) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max)
}

function normaliseArray(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function normaliseObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function stripPrice(value) {
  return clean(String(value || '').replace(PRICE_SUFFIX_RE, ''), 160)
}

export function componentIsPassportEligible(component = {}) {
  return PASSPORT_TYPE_SET.has(clean(component.type).toLowerCase())
}

export function jobIsPassportEligible(job = {}, component = null, workshop = {}) {
  if (workshop.service_passport_enabled !== true) return false
  if (!isSuspensionJob(job)) return false
  if (!componentIsPassportEligible(component)) return false
  return normaliseJobStatus(job.status) === 'completed' && jobPaymentMeta(job).key === 'paid'
}

function serviceDate(job = {}) {
  return clean(job.completed_at || job.closed_at || job.date || job.created_at).slice(0, 10)
}

function serviceLines(job = {}) {
  return normaliseArray(job.job_services)
    .filter(line => line && typeof line === 'object' && !line.is_addon)
    .map(line => stripPrice(line.name))
    .filter(Boolean)
}

function serviceName(job = {}) {
  const lineNames = serviceLines(job)
  if (lineNames.length) return lineNames.join(' + ')

  const snapshot = normaliseObject(job.service_template_snapshot)
  const snapshotName = stripPrice(snapshot.name)
  if (snapshotName) return snapshotName

  const summary = jobServiceSummary(job)
  return stripPrice(summary) || 'Suspension service'
}

function serviceType(job = {}, component = {}) {
  const categories = new Set()
  normaliseArray(job.job_services)
    .filter(line => line && typeof line === 'object' && !line.is_addon)
    .forEach(line => {
      const category = clean(line.category).toLowerCase()
      if (category) categories.add(category)
    })

  const snapshot = normaliseObject(job.service_template_snapshot)
  ;[snapshot.service_category, snapshot.component_type, component.type].forEach(value => {
    const category = clean(value).toLowerCase()
    if (category) categories.add(category)
  })

  return [...categories].filter(Boolean).join(', ') || clean(component.type)
}

function addonSnapshotName(addon = {}) {
  if (!addon || typeof addon !== 'object') return ''
  const isAddon = addon.is_addon === true
    || addon.template_kind === 'addon'
    || String(addon.service_category || '').toLowerCase().includes('upgrade_addon')
  return isAddon ? stripPrice(addon.name) : ''
}

export function passportUpgradeNames(job = {}) {
  const names = []
  normaliseArray(job.job_services)
    .filter(line => line && typeof line === 'object' && line.is_addon === true)
    .forEach(line => {
      const name = stripPrice(line.name)
      if (name) names.push(name)
    })

  normaliseArray(job.service_template_addon_snapshots)
    .forEach(addon => {
      const name = addonSnapshotName(addon)
      if (name) names.push(name)
    })

  return [...new Set(names)].slice(0, 12)
}

export function buildServicePassportEntries({ workshop = {}, jobs = [], components = [] } = {}) {
  const componentById = new Map((components || []).map(component => [component.id, component]))

  return (jobs || [])
    .map(job => {
      const component = componentById.get(job.component_id)
      if (!jobIsPassportEligible(job, component, workshop)) return null
      const date = serviceDate(job)
      if (!date) return null

      return {
        id: job.id,
        job_id: job.id,
        component_id: component.id,
        component_type: clean(component.type).toLowerCase(),
        component_brand: clean(component.brand, 80),
        component_model: clean(component.model, 120),
        component_serial: clean(component.serial, 120),
        service_name: serviceName(job),
        service_type: serviceType(job, component),
        service_date: date,
        upgrades_installed: passportUpgradeNames(job),
      }
    })
    .filter(Boolean)
    .sort((a, b) => String(b.service_date).localeCompare(String(a.service_date)))
}

export function groupPassportEntriesByComponent(entries = []) {
  return entries.reduce((groups, entry) => {
    const key = entry.component_id || 'unknown'
    if (!groups[key]) groups[key] = []
    groups[key].push(entry)
    return groups
  }, {})
}
